// src/hooks/useBackendSignedUrl.ts
// Simple hook to get signed URLs from backend

import { useState, useEffect, useCallback } from "react";
import { getCachedSignedAudioUrl } from "../lib/backend-signed-urls";
import type { SignedAudioOptions } from "../lib/backend-signed-urls";

interface UseBackendSignedUrlResult {
  signedUrl: string | null;
  loading: boolean;
  error: string | null;
  fetchSignedUrl: () => Promise<void>;
}

/**
 * Hook to get signed URL for a track from backend.
 * Supports catalog (default) and reference tracks (disco-import).
 *
 * @param trackId The track ID
 * @param autoFetch Whether to automatically fetch the signed URL when trackId changes (default: true)
 * @param options Optional SignedAudioOptions — pass kind='reference' + shareToken for reference tracks
 * @returns Object with signedUrl, loading, error states, and fetchSignedUrl function
 */
export function useBackendSignedUrl(
  trackId: string | null,
  autoFetch: boolean = true,
  options?: SignedAudioOptions
): UseBackendSignedUrlResult {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable serialisation of options so useCallback/useEffect deps don't thrash
  const optionsKey = options
    ? `${options.kind || "catalog"}-${options.shareToken || ""}-${options.format || ""}-${options.variant || ""}-${options.quality || ""}`
    : "";

  const fetchSignedUrl = useCallback(async () => {
    if (!trackId) {
      setSignedUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = await getCachedSignedAudioUrl(trackId, options ?? "mp3");

      if (url) {
        setSignedUrl(url);
        setError(null);
      } else {
        setError("Failed to get signed URL");
        setSignedUrl(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setSignedUrl(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId, optionsKey]);

  useEffect(() => {
    if (!trackId) {
      setSignedUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (autoFetch) {
      let isCancelled = false;

      const fetchUrl = async () => {
        setLoading(true);
        setError(null);

        try {
          const url = await getCachedSignedAudioUrl(trackId, options ?? "mp3");

          if (!isCancelled) {
            if (url) {
              setSignedUrl(url);
              setError(null);
            } else {
              setError("Failed to get signed URL");
              setSignedUrl(null);
            }
            setLoading(false);
          }
        } catch (err) {
          if (!isCancelled) {
            setError(err instanceof Error ? err.message : "Unknown error");
            setSignedUrl(null);
            setLoading(false);
          }
        }
      };

      fetchUrl();

      return () => {
        isCancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId, autoFetch, optionsKey]);

  return { signedUrl, loading, error, fetchSignedUrl };
}
